"use client";

import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

export default function EditUserPage () {
    const { id } = useParams();
    const [formData, setFormData] = useState({
        username: "",
        email: "",
        mobile_no: "",
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (id) {
            fetchUserData();
        } else {
            setIsLoading(false);
            toast.error("User ID is missing");
            navigate("/dashboard/user");
        }
    }, [id]);

    const fetchUserData = async () => {
        try {
            setIsLoading(true);
            setError(null);

            const response = await fetch(`https://leapatinternational.com/admin/api/v1/user.php?id=${id}`);

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const result = await response.json();

            if (result.status === "success" && result.data) {
                setFormData({
                    username: result.data[0].username || "",
                    email: result.data[0].email || "",
                    mobile_no: result.data[0].mobile_no || "",
                });
            } else {
                throw new Error(result.message || "Failed to fetch user data");
            }
        } catch (err) {
            console.error("Error fetching user:", err);
            setError(err.message || "Failed to load user data");
            toast.error("Failed to load user data. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const validateForm = () => {
        if (!formData.username.trim()) {
            toast.error("Username is required");
            return false;
        }
        if (!formData.email.trim()) {
            toast.error("Email is required");
            return false;
        }
        if (!formData.mobile_no.trim()) {
            toast.error("Mobile number is required");
            return false;
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            toast.error("Please enter a valid email address");
            return false;
        }

        // Mobile number validation (basic)
        const mobileRegex = /^[0-9]{10,15}$/;
        if (!mobileRegex.test(formData.mobile_no.replace(/\s+/g, ""))) {
            toast.error("Please enter a valid mobile number");
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);

        try {
            const data = new FormData();
            data.append("id", id);
            data.append("username", formData.username);
            data.append("email", formData.email);
            data.append("mobile_no", formData.mobile_no);

            const endpoint = "https://leapatinternational.com/admin/api/v1/user_edit.php";

            const response = await fetch(endpoint, {
                method: "POST",
                body: data,
            });

            const result = await response.json();

            if (result.status === "success") {
                toast.success("User updated successfully!");
                navigate("/dashboard/user");
            } else {
                toast.error(result.message || "Failed to update user");
            }
        } catch (err) {
            console.error("Error:", err);
            toast.error("Network error. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        navigate("/dashboard/user");
    };

    // Loading state
    if (isLoading) {
        return (
            <div className="edit-user-page">
                <div className="row">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-body">
                                <div className="text-center py-5">
                                    <div className="spinner-border" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="mt-3">Loading user data...</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="edit-user-page">
                <div className="row">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-body">
                                <div className="text-center py-5">
                                    <div className="alert alert-danger" role="alert">
                                        {error}
                                    </div>
                                    <button className="btn btn-primary mt-3" onClick={() => navigate("/dashboard/users")}>
                                        Back to Users
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="category-management-section">
            {/* Page Header */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <div>
                            <h1 className="page-title mb-0">Edit User</h1>
                            <p className="page-subtitle mb-0">Update user information</p>
                        </div>
                        <button className="back-button me-3" onClick={handleCancel}>
                            ← Back to Users
                        </button>
                    </div>
                </div>
            </div>

            {/* Form */}
            <div className="row">
                <div className="col-lg-12">
                    <div className="card">
                        <div className="card-header">
                            <h5 className="card-title mb-0">User Information</h5>
                        </div>
                        <div className="card-body">
                            <form onSubmit={handleSubmit}>
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="mb-4">
                                            <label className="form-label">User ID</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={id}
                                                disabled
                                                style={{ backgroundColor: "#f8f9fa" }}
                                            />
                                            <div className="form-text">User ID cannot be changed</div>
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="mb-4">
                                            <label className="form-label">Username *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="username"
                                                value={formData.username}
                                                onChange={handleInputChange}
                                                placeholder="Enter username"
                                                required
                                            />
                                            <div className="form-text">Choose a unique username for the user</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="mb-4">
                                            <label className="form-label">Email Address *</label>
                                            <input
                                                type="email"
                                                className="form-control"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                placeholder="Enter email address"
                                                required
                                            />
                                            <div className="form-text">Enter a valid email address</div>
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="mb-4">
                                            <label className="form-label">Mobile Number *</label>
                                            <input
                                                type="tel"
                                                className="form-control"
                                                name="mobile_no"
                                                value={formData.mobile_no}
                                                onChange={handleInputChange}
                                                placeholder="Enter mobile number"
                                                required
                                            />
                                            <div className="form-text">Enter a valid mobile number</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="d-flex gap-2 pt-3 border-top">
                                    <button type="submit" className="add-button" disabled={isSubmitting}>
                                        {isSubmitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2"></span>
                                                Updating...
                                            </>
                                        ) : (
                                            <>Update User</>
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        className="back-button pe-3 ps-3"
                                        onClick={handleCancel}
                                        disabled={isSubmitting}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
